/**
 * Sign-in state for Open in Nanome, on top of @nanome-lite/bridge oauth.ts (PKCE S256, public client).
 * Flow: discover -> registerClient (cached in localStorage) -> beginAuthorization in a 520x720 popup, full-redirect
 * fallback when the popup is blocked -> /oauth/callback (OAuthCallback.vue) runs completeAuthorization and hands
 * the token back over a BroadcastChannel (and postMessage to the opener). Token lives in sessionStorage only.
 */
import { computed, onScopeDispose, ref } from 'vue';
import { beginAuthorization, clearToken, completeAuthorization, discover, forgetClient, registerClient, storeToken, storedToken, type TokenResponse } from '@nanome-lite/bridge';

export const MARA_URL = (import.meta.env.VITE_MARA_URL ?? 'https://app.nanome.ai').replace(/\/$/, '');
export const CLIENT_NAME = 'Nanome Lite';
export const CALLBACK_PATH = '/oauth/callback';
export const CHANNEL_NAME = 'nanome-lite-oauth';
/** sessionStorage flag set before a full-page redirect so the viewer reopens the dialog after the round trip. */
export const RETURN_KEY = 'nanome-lite.oauth.return';

export type ChannelMessage = { type: 'token'; token: TokenResponse } | { type: 'error'; message: string };

export const redirectUri = () => `${location.origin}${CALLBACK_PATH}`;

export function isOAuthCallbackUrl(href = location.href): boolean {
  const u = new URL(href);
  return u.pathname === CALLBACK_PATH || u.hash.startsWith(`#${CALLBACK_PATH}`);
}

/** Runs on the callback page; returns the token after storing it in this tab's sessionStorage. */
export async function finishCallback(href = location.href): Promise<TokenResponse> {
  const u = new URL(href);
  // hash-routed fallback: '#/oauth/callback?code=…'
  const effective = u.pathname === CALLBACK_PATH ? href : `${u.origin}${CALLBACK_PATH}${u.hash.slice(CALLBACK_PATH.length + 1)}`;
  return completeAuthorization(effective);
}

/** Posts the token to the tab that started the flow (popup case). Returns true when someone could be reached. */
export function announceToken(message: ChannelMessage): boolean {
  let reached = false;
  try { const ch = new BroadcastChannel(CHANNEL_NAME); ch.postMessage(message); ch.close(); reached = true; }
  catch { /* no BroadcastChannel */ }
  try {
    if (window.opener && !window.opener.closed) { window.opener.postMessage({ channel: CHANNEL_NAME, ...message }, location.origin); reached = true; }
  }
  catch { /* cross-origin opener */ }
  return reached;
}

export function useOAuth() {
  const token = ref<string | null>(storedToken()?.access_token ?? null);
  const signingIn = ref(false);
  const error = ref<string | null>(null);
  let popup: Window | null = null;
  let poll: ReturnType<typeof setInterval> | undefined;
  let channel: BroadcastChannel | null = null;

  const isSignedIn = computed(() => !!token.value);

  function accept(message: ChannelMessage) {
    if (message.type === 'token') {
      storeToken(message.token);
      token.value = message.token.access_token;
      error.value = null;
    }
    else {
      error.value = message.message;
    }
    signingIn.value = false;
    stopWaiting();
    try { popup?.close(); } catch { /* already closed */ }
    popup = null;
  }

  function onWindowMessage(e: MessageEvent) {
    if (e.origin !== location.origin || !e.data || e.data.channel !== CHANNEL_NAME) return;
    accept(e.data as ChannelMessage);
  }

  function startWaiting() {
    try { channel = new BroadcastChannel(CHANNEL_NAME); channel.onmessage = e => accept(e.data as ChannelMessage); }
    catch { channel = null; }
    window.addEventListener('message', onWindowMessage);
    poll = setInterval(() => {
      // the popup may have been closed by hand; also re-read sessionStorage in case the callback stored it here
      const stored = storedToken();
      if (stored) { accept({ type: 'token', token: stored }); return; }
      if (popup && popup.closed) { signingIn.value = false; error.value = 'The sign-in window was closed before completing'; stopWaiting(); popup = null; }
    }, 500);
  }

  function stopWaiting() {
    if (poll) clearInterval(poll);
    poll = undefined;
    channel?.close();
    channel = null;
    window.removeEventListener('message', onWindowMessage);
  }

  /** `returnTo` tells the viewer what to reopen after the full-redirect fallback ('prompt' keeps the prompt bar draft). */
  async function signIn(returnTo: 'open-in-nanome' | 'prompt' = 'open-in-nanome') {
    if (signingIn.value) return;
    signingIn.value = true;
    error.value = null;
    try {
      const meta = await discover(MARA_URL);
      const uri = redirectUri();
      const client = await registerClient(meta, CLIENT_NAME, uri);
      const url = await beginAuthorization(meta, client, uri);
      popup = window.open(url, 'nanome-lite-oauth', 'popup=yes,width=520,height=720');
      if (popup) { startWaiting(); return; }
      // popup blocked: full-page redirect; the viewer reopens the dialog on return (RETURN_KEY)
      sessionStorage.setItem(RETURN_KEY, returnTo);
      location.assign(url);
    }
    catch (e) {
      signingIn.value = false;
      error.value = e instanceof Error ? e.message : String(e);
    }
  }

  /** On invalid_client (a MARA restart dropped the dynamic registration) forget the cached client and retry once. */
  async function forgetAndRetry() {
    try { const meta = await discover(MARA_URL); forgetClient(meta, redirectUri()); } catch { /* ignore */ }
    await signIn();
  }

  function signOut() { clearToken(); token.value = null; }

  onScopeDispose(stopWaiting);

  return { token, isSignedIn, signingIn, error, signIn, signOut, forgetAndRetry };
}
